import type { JsonFailure, JsonListenResponse, JsonSuccess } from './types';
import { isErrorCommand, isOkCommand, WS_ERROR_COMMAND, WS_OK_COMMAND } from './types';

export interface GenericError {
  error: boolean;
  errorCommand?: string;
  title: string;
  description: string;
}

interface ErrorMessage {
  title: string;
  getDescription?: (json: JsonFailure) => string;
}

type ErrorMessageMap = Record<string, ErrorMessage>;

const errorMessageMap: ErrorMessageMap = {};

export const registerErrorMessage = (command: string, errorMessage: ErrorMessage) => {
  errorMessageMap[command] = errorMessage;
};

export const onError = (json: JsonFailure): GenericError | null => {
  const { errorCommand } = json;
  const errMsg = errorMessageMap[errorCommand];

  if (errMsg) {
    return {
      error: true,
      errorCommand,
      title: errMsg.title,
      description: errMsg.getDescription
        ? errMsg.getDescription(json)
        : 'Sorry, an unexpected error has occurred',
    };
  }

  return null;
};

export const onParseError = (err: unknown): GenericError => {
  console.error('JSON.parse error', err);
  return {
    error: true,
    title: `Error on JSON.parse`,
    description: String(err),
  };
};

// Type guard functions
const isJsonListenResponse = <T extends string, V extends object | null | undefined>(
  obj: unknown,
): obj is JsonListenResponse<T, V> => {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'command' in obj &&
    typeof (obj as Record<string, unknown>).command === 'string'
  );
};

const isJsonFailure = (obj: unknown): obj is JsonFailure => {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'command' in obj &&
    (obj as Record<string, unknown>).command === WS_ERROR_COMMAND &&
    'errorCommand' in obj &&
    typeof (obj as Record<string, unknown>).errorCommand === 'string'
  );
};

const isJsonSuccess = (obj: unknown): obj is JsonSuccess => {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'command' in obj &&
    (obj as Record<string, unknown>).command === WS_OK_COMMAND &&
    'okCommand' in obj &&
    typeof (obj as Record<string, unknown>).okCommand === 'string'
  );
};

interface ParseResponseResult<T> {
  json: T | null | undefined;
  error?: GenericError | null;
  errorCommand?: string;
  okCommand?: string;
  skipProcessing: boolean;
}

export const parseResponse = <T extends object | undefined | null = undefined | null>(
  payload: { message: string },
  allowedCommands: string[] = [],
): ParseResponseResult<T> => {
  const result: ParseResponseResult<T> = {
    json: null,
    skipProcessing: false,
  };

  try {
    const parsedJson: unknown = JSON.parse(payload.message);

    if (!isJsonListenResponse<(typeof allowedCommands)[number], T>(parsedJson)) {
      result.json = undefined;
      result.error = onParseError(new Error('Invalid JSON response format'));
      result.skipProcessing = false;
      return result;
    }

    const json = parsedJson;
    const { command } = json;

    if (![...allowedCommands, WS_OK_COMMAND, WS_ERROR_COMMAND].includes(command)) {
      result.json = json;
      result.skipProcessing = true;
      return result;
    }

    if (isErrorCommand(command)) {
      if (!isJsonFailure(json)) {
        result.json = json;
        result.skipProcessing = true;
        return result;
      }

      const { errorCommand } = json;
      if (errorCommand && !allowedCommands.includes(errorCommand)) {
        result.json = json;
        result.skipProcessing = true;
        return result;
      }

      result.json = json;
      result.error = onError(json);
      result.errorCommand = errorCommand;
      result.skipProcessing = false;
      return result;
    }

    if (isOkCommand(command)) {
      if (!isJsonSuccess(json)) {
        result.json = json;
        result.skipProcessing = true;
        return result;
      }

      const { okCommand } = json;
      if (okCommand && !allowedCommands.includes(okCommand)) {
        result.json = json;
        result.skipProcessing = true;
        return result;
      }

      result.json = json;
      result.okCommand = okCommand;
      result.skipProcessing = false;
      return result;
    }

    result.json = json;
    result.skipProcessing = false;
    return result;
  } catch (err: unknown) {
    result.json = undefined;
    result.error = onParseError(err);
    result.skipProcessing = false;
    return result;
  }
};

export type { ParseResponseResult };
